import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
/** The top of the life-balance scale: a domain that got its full daily ideal scores 6. */
export const LIFE_BALANCE_MAX_SCORE = 6;
export const dailyLifeBalanceDomainSchema = z.object({
    lifeDomainId: z.string(),
    name: z.string(),
    /** Zendia's score, uncapped (above 6 means more than the ideal). Null until computed. */
    computedScore: z.number().nullable(),
    /** The whole-number score the user set, which overrides the computed one for display. */
    userScore: z.number().nullable(),
    minutes: z.number().nullable(),
    idealMinutes: z.number().nullable(),
});
export const dailyLifeBalanceResponseSchema = z.object({
    date: z.string(),
    status: z.enum(['ready', 'pending']),
    domains: z.array(dailyLifeBalanceDomainSchema),
});
export const lifeBalanceSummaryDomainSchema = z.object({
    lifeDomainId: z.string(),
    name: z.string(),
    /** 6 x minutes / idealMinutes over the range, uncapped. Null when there is no ideal. */
    score: z.number().nullable(),
    minutes: z.number(),
    idealMinutes: z.number(),
});
export const lifeBalanceSummaryResponseSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
    /** Days in the range with a computed balance or a score the user set. */
    dayCount: z.number(),
    domains: z.array(lifeBalanceSummaryDomainSchema),
});
export const habitLifeDomainSuggestionsResponseSchema = z.object({
    suggestions: z.record(z.string(), z.string()),
});
/** Query key for one day's life balance, shared by the query and the save mutation. */
export const getDailyLifeBalanceQueryKey = (date) => ['lifeBalance', 'daily', date];
/** Every range summary, for invalidating them all when a day changes. */
export const lifeBalanceSummaryQueryKeyPrefix = ['lifeBalance', 'summary'];
export const getLifeBalanceSummaryQueryKey = ({ startDate, endDate }) => [...lifeBalanceSummaryQueryKeyPrefix, startDate, endDate];
export const habitLifeDomainSuggestionsQueryKey = ['lifeBalance', 'habitSuggestions'];
/** When to refetch suggestions after a habit save: the AI usually answers within seconds. */
const SUGGESTION_REFRESH_DELAYS_MS = [4_000, 15_000];
/**
 * Refetches the habit suggestions a few seconds after a habit is saved, once the background
 * classification has had time to answer. Called from the habit create and update mutations.
 */
export const refreshHabitLifeDomainSuggestionsSoon = (queryClient) => {
    for (const delay of SUGGESTION_REFRESH_DELAYS_MS) {
        setTimeout(() => queryClient.invalidateQueries({ queryKey: habitLifeDomainSuggestionsQueryKey }), delay);
    }
};
/**
 * A score as the chart shows it: rounded to a whole number and capped at 6. Stored scores are
 * not capped, so a day well over the ideal still reads as a full 6 here. No score shows as 0.
 */
export const chartLifeBalanceScore = (score) => Math.min(LIFE_BALANCE_MAX_SCORE, Math.max(0, Math.round(score ?? 0)));
/** The score the chart shows for a day's domain: the user's own when they set one, else Zendia's. */
export const displayLifeBalanceScore = (domain) => chartLifeBalanceScore(domain.userScore ?? domain.computedScore);
/**
 * Returns a copy of a cached day with the user's scores merged in, for the save mutation's
 * optimistic update. Anything that isn't a successful response with data is returned untouched.
 */
export const setLifeBalanceUserScores = (response, scores) => {
    if (!response?.data)
        return response;
    return {
        ...response,
        data: {
            ...response.data,
            domains: response.data.domains.map((domain) => domain.lifeDomainId in scores
                ? { ...domain, userScore: scores[domain.lifeDomainId] }
                : domain),
        },
    };
};
/** One day's life balance: Zendia's computed scores and the user's own, per life domain. */
export const useGetDailyLifeBalance = (date, authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: getDailyLifeBalanceQueryKey(date),
        queryFn: () => authenticatedRequest({ url: `/life-balance/daily/${date}`, method: 'GET' }, dailyLifeBalanceResponseSchema),
        enabled: autoFetch && !!date,
    });
};
/** Saves the user's own scores for a day, showing them straight away. */
export const useSaveLifeBalanceUserScores = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ date, scores }) => authenticatedRequest({ url: `/life-balance/daily/${date}/user-scores`, method: 'PUT', data: { scores } }, dailyLifeBalanceResponseSchema),
        onMutate: async ({ date, scores }) => {
            const queryKey = getDailyLifeBalanceQueryKey(date);
            // Stop an in-flight refetch from landing on top of the optimistic value.
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (current) => setLifeBalanceUserScores(current, scores));
            return { previous };
        },
        onError: (error, { date }, context) => {
            if (context?.previous) {
                queryClient.setQueryData(getDailyLifeBalanceQueryKey(date), context.previous);
            }
        },
        // Returned so a caller awaiting mutateAsync only settles once the refetch has landed. The
        // user's scores feed every range summary that includes the day, so those are refetched too.
        onSettled: (data, error, { date }) => Promise.all([
            queryClient.invalidateQueries({ queryKey: getDailyLifeBalanceQueryKey(date) }),
            queryClient.invalidateQueries({ queryKey: lifeBalanceSummaryQueryKeyPrefix }),
        ]),
    });
};
/** The user's life balance per domain over a range of their days, for Insights. */
export const useGetLifeBalanceSummary = (dateRange, authenticatedRequest) => {
    return useQuery({
        queryKey: getLifeBalanceSummaryQueryKey(dateRange ?? { startDate: '', endDate: '' }),
        queryFn: () => authenticatedRequest({ url: '/life-balance/summary', method: 'GET', params: { ...dateRange } }, lifeBalanceSummaryResponseSchema),
        enabled: !!dateRange && dateRange.startDate <= dateRange.endDate,
    });
};
/**
 * The life domain the AI suggests for each habit, by habit id. Habits are classified shortly
 * after they are saved, so callers refetch a few seconds after a save to pick up a new one.
 */
export const useGetHabitLifeDomainSuggestions = (authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: habitLifeDomainSuggestionsQueryKey,
        queryFn: () => authenticatedRequest({ url: '/life-balance/habit-suggestions', method: 'GET' }, habitLifeDomainSuggestionsResponseSchema),
        enabled: autoFetch,
    });
};
